/**
 * Adatta la mappa piatta che arriva dall'API alla forma annidata che vue-i18n
 * si aspetta: `{'a.b': 'x'}` diventa `{a: {b: 'x'}}`.
 *
 * Non si usa l'opzione `flatJson` di vue-i18n: muta in posto l'oggetto che le si
 * passa, salta in silenzio i conflitti e non è esportata, quindi non è testabile
 * da qui. Questa funzione è pura e coperta da test.
 */

export type NestedMessages = { [key: string]: string | NestedMessages }

/**
 * Segmenti di chiave che non possono diventare proprietà.
 *
 * Le chiavi arrivano da database, cioè da un input remoto: una label chiamata
 * `__proto__.x` scriverebbe sul prototipo di Object. Nessuna chiave legittima ha
 * questi nomi, quindi si scartano.
 */
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype'])

/**
 * Rende sicuro per vue-i18n un testo scritto da chi traduce.
 *
 * `@` apre la sintassi dei linked message e `|` separa le forme plurali: una
 * traduzione che li contiene nudi **fa fallire il render a runtime**, non
 * degrada. A database si tiene il testo naturale (`you@example.com`), così nel
 * pannello si scrive quello che si vede; l'escape lo rimette qui.
 *
 * Prima si normalizza l'eventuale forma già sfuggita, così applicare la funzione
 * due volte non produce `{'{'@'}'}`: se qualcuno scrive `{'@'}` nel pannello,
 * perché conosce vue-i18n, il risultato è comunque corretto.
 */
export function escapeMessage(value: string): string {
  return value.replace(/\{'(@|\|)'\}/g, '$1').replace(/[@|]/g, (char) => `{'${char}'}`)
}

export function unflattenMessages(flat: Record<string, string>): NestedMessages {
  const root: NestedMessages = {}

  for (const [key, value] of Object.entries(flat)) {
    if (typeof value !== 'string') continue

    const segments = key.split('.').filter((segment) => segment.length > 0)
    if (segments.length === 0) continue
    if (segments.some((segment) => FORBIDDEN_SEGMENTS.has(segment))) continue

    const leaf = segments[segments.length - 1] as string
    let node = root
    let conflicted = false

    for (const segment of segments.slice(0, -1)) {
      const existing = node[segment]

      if (existing === undefined) {
        const created: NestedMessages = {}
        node[segment] = created
        node = created
        continue
      }

      if (typeof existing === 'string') {
        // Esistono sia la label `a` sia la label `a.b`: l'una non può essere
        // contemporaneamente testo e ramo. Si tiene la prima e si scarta questa.
        conflicted = true
        break
      }

      node = existing
    }

    if (conflicted) {
      warnConflict(key)
      continue
    }

    if (typeof node[leaf] === 'object') {
      warnConflict(key)
      continue
    }

    node[leaf] = escapeMessage(value)
  }

  return root
}

function warnConflict(key: string): void {
  if (import.meta.env.DEV) {
    console.warn(`[i18n] chiave in conflitto con un'altra label, ignorata: ${key}`)
  }
}
